import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Mail } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Button } from "../components/ui/button";
import { PARTNERS, CLIENTS } from "../mock";
import { PRODUCTS_DATA } from "../data/productsData";

const ProductPage = () => {
  const { slug } = useParams();
  const data = PRODUCTS_DATA[slug];

  if (!data) return <Navigate to="/offerings/all" replace />;

  const { eyebrow, title, subtitle, whyMatters, whatCards, capabilities, howSteps, whoItems, ctaHeadline } = data;

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* 1) HERO */}
      <PageHero eyebrow={eyebrow} title={title} subtitle={subtitle} />

      <section className="max-w-7xl mx-auto px-4 pt-10 pb-6">
        <Reveal>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/contact">
              <Button className="bg-[#15348C] hover:bg-[#0A1F5C] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#15348C]/25">
                Talk to Our Expert <ArrowRight size={17} className="ml-1" />
              </Button>
            </Link>
            <Link to="/demo">
              <Button className="bg-white text-[#15348C] border-2 border-[#15348C] hover:bg-[#15348C] hover:text-white font-semibold px-6 h-12 rounded-md">
                Book a Demo
              </Button>
            </Link>
          </div>
        </Reveal>
      </section>

      {/* 2) WHY IT MATTERS */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">WHY IT MATTERS</div>
              <h2 className="text-[#15348C] leading-tight">The gaps teams face without it.</h2>
            </div>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {whyMatters.map((w, i) => (
              <Reveal key={w} variant="up" delay={i * 90}>
                <div className="bg-white border border-gray-100 rounded-2xl p-6 h-full hover:shadow-lg transition-all">
                  <div className="w-10 h-10 rounded-lg bg-[#F57C20]/15 flex items-center justify-center mb-4">
                    <span className="text-[#F57C20] font-bold" style={{ fontFamily: "Times New Roman, serif" }}>{i + 1}</span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{w}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3) WHAT IT IS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl">
              <div className="text-xs font-bold tracking-widest text-[#6BBE32] mb-3">WHAT IT IS</div>
              <h2 className="text-[#15348C] leading-tight">Built around three connected pillars.</h2>
            </div>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {whatCards.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.title} variant="up" delay={i * 100}>
                  <div className="group bg-white border border-gray-100 rounded-2xl p-7 h-full hover:border-[#6BBE32]/40 hover:shadow-xl transition-all">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#6BBE32] to-[#4F9622] flex items-center justify-center shadow-lg shadow-[#6BBE32]/30 mb-5">
                      <Icon size={26} className="text-white" />
                    </div>
                    <h3 className="text-[#15348C] group-hover:text-[#6BBE32] transition-colors" style={{ fontSize: "1.35rem" }}>{c.title}</h3>
                    <p className="mt-3 text-gray-600 leading-relaxed">{c.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4) KEY CAPABILITIES */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl mb-12">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">KEY CAPABILITIES</div>
              <h2 className="text-[#15348C] leading-tight">Everything built in, from day one.</h2>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.title} variant="up" delay={i * 80}>
                  <div className="bg-white border border-gray-100 rounded-2xl p-6 h-full hover:shadow-lg transition-all">
                    <div className="w-11 h-11 rounded-xl bg-[#15348C]/10 flex items-center justify-center mb-4">
                      <Icon size={20} className="text-[#15348C]" />
                    </div>
                    <h3 className="text-[#15348C]" style={{ fontSize: "1.05rem" }}>{c.title}</h3>
                    <p className="mt-2 text-gray-600 text-sm leading-relaxed">{c.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5) HOW IT WORKS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl mb-10">
              <div className="text-xs font-bold tracking-widest text-[#6BBE32] mb-3">HOW IT WORKS</div>
              <h2 className="text-[#15348C] leading-tight">From first launch to outcomes, in four steps.</h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howSteps.map((step, i) => (
              <Reveal key={step} variant="up" delay={i * 90}>
                <div className="relative bg-white border border-gray-100 rounded-2xl p-6 h-full hover:shadow-lg transition-all">
                  <div className="absolute -top-4 left-6 w-10 h-10 rounded-full bg-[#15348C] text-white flex items-center justify-center font-bold shadow-md" style={{ fontFamily: "Times New Roman, serif" }}>
                    {i + 1}
                  </div>
                  <p className="mt-4 text-gray-700 leading-relaxed">{step}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6) PROOF STRIP */}
      <section className="py-16 bg-[#F8F9FA] border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center text-xs font-bold tracking-widest text-gray-500 mb-6">TRUSTED BY GLOBAL ENTERPRISES &amp; VENDORS</div>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5 opacity-80">
              {PARTNERS.slice(0, 10).map((p) =>
                p.logo ? (
                  <img key={p.name} src={p.logo} alt={p.name} className="h-8 max-w-[120px] object-contain" />
                ) : (
                  <span key={p.name} className="text-[#15348C] font-bold text-sm">{p.name}</span>
                )
              )}
            </div>
            {CLIENTS && CLIENTS.length > 0 && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-70">
                {CLIENTS.slice(0, 8).map((c) =>
                  c.logo ? (
                    <img key={c.name} src={c.logo} alt={c.name} className="h-7 max-w-[110px] object-contain" />
                  ) : (
                    <span key={c.name} className="text-gray-500 font-semibold text-sm">{c.name}</span>
                  )
                )}
              </div>
            )}
          </Reveal>
        </div>
      </section>

      {/* 7) WHO IT'S FOR */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="max-w-3xl mb-10">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">WHO IT&apos;S FOR</div>
              <h2 className="text-[#15348C] leading-tight">Built for the teams that need it most.</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {whoItems.map((w, i) => (
              <Reveal key={w} variant="up" delay={i * 90}>
                <div className="flex gap-4 bg-white border border-gray-100 rounded-2xl p-6 h-full hover:shadow-lg transition-all">
                  <CheckCircle2 size={22} className="text-[#6BBE32] shrink-0 mt-0.5" />
                  <p className="text-gray-700 leading-relaxed">{w}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 8) CTA BAND */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#15348C] to-[#0A1F5C] px-8 py-12 lg:px-14 lg:py-16 text-white">
              <div className="absolute -top-24 -right-24 w-80 h-80 bg-[#6BBE32]/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-[#F57C20]/20 rounded-full blur-3xl" />
              <div className="relative grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-white leading-tight">{ctaHeadline}</h2>
                </div>
                <div className="flex flex-wrap gap-4 lg:justify-end items-center">
                  <Link to="/contact">
                    <Button className="bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-7 h-12 rounded-md shadow-lg shadow-[#F57C20]/30">
                      Talk to Our Expert <ArrowRight size={17} className="ml-1" />
                    </Button>
                  </Link>
                  <a href="mailto:info@skillsgain.com" className="inline-flex items-center gap-2 text-white/90 hover:text-[#8BD835] font-semibold transition-colors">
                    <Mail size={18} /> info@skillsgain.com
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductPage;
